import React, { useEffect, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid } from 'react-bootstrap';
import qs from 'qs';

import { ReactComponent as AppIcon } from './icons/2fa-app.svg';
import Loader from '../../../components/Loaders/Loader';

import { getAccessRoutes } from '../../../routers/helpers';
import { getNavbarConfig, getNavbarCounters } from '../../../services/endpoints/general';
import { delay, parseUrl, isUrlEditor, getErrorMessage } from '../../../utils/helper';
import { userAuthSuccess, userLogoutCredentialsAction } from '../../../redux/actions/user';
import { WebsocketContext } from '../../../utils/WebsocketProvider';
import { authCheck, authConfirmApp, authReject } from '../../../services/endpoints/auth';
import * as actionTypes from '../../../redux/types/header';
import * as actionTypesGeneral from '../../../redux/types/general';

let checkInterval = null;
let checkTimeout = null;
const CHECK_INTERVAL = 5000;
const Page2FaApp = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isAuth } = useSelector((state) => state.user);
  const { initWebsockets } = useContext(WebsocketContext);

  const [authCode, setAuthCode] = useState('');
  const [isLoading, setIsLoading] = useState({
    state: false,
    success: false,
  });

  const query = qs.parse(window.location.search.slice(1), {
    delimiter: ';',
  });

  const clearTimers = () => {
    clearTimeout(checkTimeout);
    clearInterval(checkInterval);
    checkTimeout = null;
    checkInterval = null;
  };

  const fetchNavbarConfig = async () => {
    try {
      const response = await Promise.all([getNavbarConfig(), getNavbarCounters()]);
      const [config, counters] = response;
      const menuWithCounters = config.data?.menu?.map((item) => {
        const finded = counters?.data?.find((c) => c.code === item.code);
        if (finded) {
          return {
            ...item,
            count: finded.count,
          };
        }
        return item;
      });
      dispatch({
        type: actionTypes.GET_NAVBAR_SUCCESS,
        data: config.data,
        menu: menuWithCounters,
      });
      dispatch({
        type: actionTypesGeneral.CHANGE_VIEW_MODE_SUCCESS,
        payload: config.data.dark_mode,
      });

      return config.data;
    } catch (error) {
      console.log(error);
    }
  };

  const onConfirmCode = async (code) => {
    setIsLoading((prevState) => ({
      ...prevState,
      state: true,
    }));
    try {
      const response = await authConfirmApp({ code });
      if (response.data?.access_token) {
        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const onGetAuthStatus = async (data) => {
    if (data.status === 'pending') {
      if (data?.time_left < 0) {
        clearTimers();
        setIsLoading((prevState) => ({
          ...prevState,
          success: false,
        }));
        localStorage.removeItem('2fa_code');
        await delay(500);
        history.replace({ pathname: '/2fa-rejected', state: { fromApp: true }, search: history.location.search });
        return;
      }
    }
    if (data.status === 'success') {
      clearTimers();
      const response = await onConfirmCode(data?.code);
      if (!response) {
        setIsLoading((prevState) => ({
          ...prevState,
          success: false,
        }));
        localStorage.removeItem('2fa_code');
        await delay(500);
        history.replace({ pathname: '/2fa-rejected', state: { fromApp: true }, search: history.location.search });
        return;
      }
      const navbar = await fetchNavbarConfig();
      const firstRoute = navbar?.menu[0];
      await delay(500);
      await dispatch(userAuthSuccess());
      const allAccessRoutes = getAccessRoutes();
      const queryRedirect = query?.redirect ? query.redirect.replace(/^(admin)|(\/admin)/gm, '') : null;
      const url = parseUrl(queryRedirect);
      const firstUrl = firstRoute?.url ? `/${firstRoute?.url}` : '/users';
      const findRoute = allAccessRoutes?.find((r) => {
        if (url) {
          return r.link === url.pathname;
        }
        return false;
      });
      const redirect = query.redirect === '/' || query.redirect === '/2fa' ? firstUrl : findRoute?.link || firstUrl;
      const isEditor = isUrlEditor(query.redirect);
      setIsLoading((prevState) => ({
        ...prevState,
        success: true,
      }));
      localStorage.removeItem('2fa_code_time');
      localStorage.removeItem('2fa_code');
      isEditor
        ? (window.location.href = '/admin/editor')
        : history.replace({
          pathname: redirect,
          search: url?.query,
          hash: url?.fragment ? `#${url.fragment}` : null,
        });
    }
    if (data.status === 'incorrect' || data.status === 'reject' || data.status === 'expired') {
      clearTimers();
      setIsLoading((prevState) => ({
        ...prevState,
        success: false,
      }));
      localStorage.removeItem('2fa_code');
      await delay(500);
      history.replace({ pathname: '/2fa-rejected', state: { fromApp: true }, search: history.location.search });
    }
  };

  const onCheckAuth = async () => {
    try {
      const response = await authCheck();
      onGetAuthStatus(response.data);
    } catch (error) {
      clearTimers();
      localStorage.removeItem('2fa_code');
      localStorage.removeItem('2fa_code_time');
      const errorMessage = getErrorMessage(error);
      dispatch(userLogoutCredentialsAction(errorMessage));
    }
  };

  const runCheckAuth = () => {
    checkInterval = setInterval(() => {
      onCheckAuth();
    }, CHECK_INTERVAL);
  };

  const onReject = async () => {
    setIsLoading((prevState) => ({
      ...prevState,
      state: true,
    }));
    try {
      await authReject();
      localStorage.removeItem('2fa_code_time');
      localStorage.removeItem('2fa_code');
      setIsLoading((prevState) => ({
        ...prevState,
        state: false,
      }));
      history.replace({ pathname: '/2fa', search: history.location.search });
    } catch (error) {
      localStorage.removeItem('2fa_code_time');
      localStorage.removeItem('2fa_code');
      setIsLoading(false);
      const errorMessage = getErrorMessage(error);
      dispatch(userLogoutCredentialsAction(errorMessage));
    }
  };

  const redirectToEmail = (e) => {
    e.preventDefault();
    clearInterval(checkInterval);
    checkInterval = null;
    onReject();
  };

  const onSocketMessage = (data) => {
    if (data) {
      switch (data?.name) {
      case 'in-app-2fa-status':
        onGetAuthStatus({ ...data?.options, status: data?.options?.result });
        break;

      default:
        break;
      }
    }
  };

  const checkInit = () => {
    const access_token = localStorage.getItem('access_token');
    /**
     * ✓ - Авторизация
     */
    if (isAuth) {
      history.replace({
        pathname: '/',
        search: history.location.search,
      });
      return;
    }

    if (window.appConfig?.second_factor && access_token && window?.appConfig?.websocket_url) {
      setAuthCode(window.appConfig?.second_factor?.code || localStorage.getItem('2fa_code'));
      let socket = {};
      try {
        const initSocket = initWebsockets(window?.appConfig?.websocket_url, onSocketMessage);
        if (initSocket?.readyState === 1) {
          socket = { ...initSocket };
        }
        initSocket.onerror = () => {
          socket = initWebsockets(window?.appConfig?.reserve_websocket_url, onSocketMessage);
          if (socket?.readyState !== 1) {
            runCheckAuth();
          }
        };
      } catch (error) {
        socket = {};
      }
      if (window.appConfig?.second_factor?.time_left > 0) {
        checkTimeout = setTimeout(() => {
          clearInterval(checkInterval);
          checkInterval = null;
          onCheckAuth();
        }, Number(window.appConfig?.second_factor?.time_left * 1000) + 500);
      }
      return;
    }

    history.replace({
      pathname: '/',
      search: history.location.search,
    });
  };

  useEffect(() => {
    checkInit();
    document.title = '2FA with App';

    return () => {
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid bsClass="container">
      <div className="page-2fa page-2fa__app">
        {isLoading.state ? <Loader success={isLoading.success} /> : null}
        <div className="page-2fa__app-container">
          <div>
            <AppIcon />
          </div>
          <div className="title">Two-factor authentication</div>
          <div className="mb-8">A push notification has been sent to your device.</div>
          <div>
            Open the
            {' '}
            <b>Panel Admin App</b>
            {' '}
            and enter this number to sign in.
          </div>
          <div className="code">
            <div className="text">{authCode}</div>
          </div>
          <div>
            <a href="# " onClick={(e) => redirectToEmail(e)} className="text-decoration-none">
              Sign in with email
            </a>
          </div>
        </div>
      </div>
    </Grid>
  );
};

export default Page2FaApp;
